import React, { useState, useEffect } from "react";
import { Warning } from "@phosphor-icons/react";
import { useOneButtonStore } from "../../../../store/oneButtonStore";
import DashboardModal from "./DashboardModal";
import './oneButton-css/DesignHistoryModalV2.css';
import { getFinanceSummaryDisplay } from "../../../../helpers/financeSelectors";
import designServices from '../../../../services/oneButtonServices/designServices';

interface DesignHistoryItem {
    Design_Id: number;
    Design_Name: string;
    Design_Json: string;
    Active_Ind: boolean;
    User_Modified_Id: string;
    User_Modified_Timestamp: string;
}

interface Props {
    open: boolean;
    onClose: () => void;
    opportunityId?: number;
}

function isLightReachTiltValid(array: any, financePartner?: string): boolean {
  if (!array) return true;

  // Only enforce for LightReach finance partner
  if (financePartner !== "Lightreach") return true;

  const tilt = Number(array.Tilt);
  if (!Number.isFinite(tilt)) return false;

  return tilt >= 5 && tilt <= 50;
}

const DesignHistoryModalV2: React.FC<Props> = ({ open, onClose, opportunityId, }) => {
    const theme = useOneButtonStore((s) => s.theme);

    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    // console.log("opportunityDetails", opportunityDetails);
    const selectedFinanceCells = useOneButtonStore((s) => s.selectedFinanceCells);
    const financingGrid = useOneButtonStore((s) => s.financingGrid);

    const financeSummaryDisplay = getFinanceSummaryDisplay(
        opportunityDetails,
        selectedFinanceCells,
        financingGrid
    );
    const financePartner = financeSummaryDisplay?.partner ?? "—";
    // console.log("financePartner", financePartner);

    const equipmentMasterData = useOneButtonStore((s) => s.equipmentMasterData);
    const getRackingPartNames = (ids: any[] | null | undefined): string => {
        if (!Array.isArray(ids) || !equipmentMasterData?.RackingParts) return '';
        return ids
            .map(id => {
                const match = equipmentMasterData.RackingParts.find((p: any) => p.Racking_Parts_Id === id || p.Racking_Part_Id === id);
                return match ? match.Racking_Part_Name : `Unknown (${id})`;
            })
            .join(', ');
    };

    const [selectedDesignIndex, setSelectedDesignIndex] = useState<number | null>(null);
    const [arrays, setArrays] = useState<any[]>([]);
    const [selectedArrayIndex, setSelectedArrayIndex] = useState(0);

    const [designHistory, setDesignHistory] = useState<DesignHistoryItem[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || !opportunityId) return;

        setLoading(true);

        designServices
            .GetDesignHistory(opportunityId)
            .then((history: any) => {
                setDesignHistory(history || []);
            })
            .catch(() => {
                setDesignHistory([]);
            })
            .finally(() => setLoading(false));
    }, [open, opportunityId]);    
    // console.log("designHistory", designHistory);

    useEffect(() => {
        if (!open || !designHistory.length) return;

        const index =
            designHistory.findIndex(d => d.Active_Ind) >= 0
                ? designHistory.findIndex(d => d.Active_Ind)
                : 0;

        const d = designHistory[index];

        setSelectedDesignIndex(index);
        setSelectedArrayIndex(0);
        setArrays(JSON.parse(d.Design_Json || "[]"));
    }, [open, designHistory]);

    const handleDesignClick = (index: number, d: DesignHistoryItem) => {
        setSelectedDesignIndex(index);
        setSelectedArrayIndex(0);

        try {
            const parsed = JSON.parse(d.Design_Json || "[]");
            setArrays(Array.isArray(parsed) ? parsed : []);
        } catch {
            setArrays([]);
        }
    };

    
    const selectedArray = arrays[selectedArrayIndex];
    // console.log("selectedArray", selectedArray);

    const isTiltValid = isLightReachTiltValid(selectedArray, financePartner);

    return (
        <DashboardModal
            open={open}
            onClose={onClose}
            title="Design History - oneBUTTON"
            footer={null}
            width={1100}
            centered
            maskClosable={false}
            theme={theme}
            destroyOnClose
            className={`obd-design-history-modal obd-theme-${theme}`}
            rootClassName={`obd-design-history-modal-root obd-theme-${theme}`}
        >
            <div className="dhv2-root">
                {/* LEFT: Design list */}
                <div className="dhv2-history">
                    {loading ? (
                        <div className="dhv2-empty">Loading...</div>
                    ) : designHistory.length === 0 ? (                        
                        <div className="dhv2-empty">No design history available</div>
                    ) : (
                        <table className="dhv2-table">
                            <thead>
                                <tr>
                                    <th>Design</th>
                                    <th>Updated By</th>
                                    <th>Updated Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {designHistory.map((d, i) => (
                                    <tr
                                        key={d.Design_Id}
                                        onClick={() => handleDesignClick(i, d)}
                                        className={[
                                            d.Active_Ind ? "dhv2-row-active-ind" : "",
                                            i === selectedDesignIndex ? "dhv2-row-selected" : "",
                                        ]
                                            .filter(Boolean)
                                            .join(" ")}
                                    >
                                        <td>{`Design ${designHistory.length - i}`}</td>
                                        <td>{d.User_Modified_Id}</td>
                                        <td>
                                            {

                                            new Date(d.User_Modified_Timestamp + "Z")
                                                .toLocaleString(undefined, {
                                                    timeZone: "America/New_York",
                                                    year: "numeric",
                                                    month: "numeric",
                                                    day: "numeric",
                                                    hour: "numeric",
                                                    minute: "2-digit",
                                                    hour12: true,
                                                })
                                                .replace(" ", "")
                                            }
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* RIGHT: Array view */}
                <div className="dhv2-array">
                    {designHistory.length === 0 ? null : arrays.length === 0 ? (
                        <div className="dhv2-empty">
                            Select a design to view array details
                        </div>
                    ) : (
                        <>
                            <div className="dhv2-array-tabs">
                                {arrays.map((_: any, i: number) => (
                                    <button
                                        key={i}
                                        type="button"
                                        onClick={() => setSelectedArrayIndex(i)}
                                        className={[
                                            "dhv2-array-tab",
                                            i === selectedArrayIndex ? "dhv2-array-tab--active" : "",
                                        ].join(" ")}
                                        style={!isLightReachTiltValid(arrays[i], financePartner) ? { border: '2px solid red' } : {}}
                                    >
                                        ARRAY {i + 1}
                                    </button>
                                ))}
                            </div>

                            {selectedArray && (
                                <div className="dhv2-array-table">
                                    <div className="dhv2-array-row">
                                        <div>
                                            <label>Module Manufacturer</label>
                                            <span>{selectedArray.ModuleManufactuterName ?? "—"}</span>
                                        </div>
                                        <div>
                                            <label>Module Model</label>
                                            <span>{selectedArray.ModuleModel ?? "—"}</span>
                                        </div>
                                        <div>
                                            <label>Module Wattage</label>
                                            <span>{selectedArray.ModuleWattage ?? "—"}</span>
                                        </div>
                                    </div>

                                    <div className="dhv2-array-row">
                                        <div>
                                            <label>Inverter Manufacturer</label>
                                            <span>
                                                {selectedArray.Inverters?.[0]?.ManufacturerName ?? "—"}
                                            </span>
                                        </div>
                                        <div>
                                            <label>Inverter Model</label>
                                            <span>
                                                {selectedArray.Inverters?.[0]?.Model
                                                    ? `${selectedArray.Inverters[0].Model}`
                                                    : "—"}
                                            </span>
                                        </div>
                                        <div>
                                            <label>Inverter Quantity</label>
                                            <span>
                                                {selectedArray.Inverters?.[0]?.Quantity ?? "—"}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="dhv2-array-row">
                                        <div>
                                            <label>Optimizer</label>
                                            <span>
                                                {selectedArray.Inverters?.[0]?.OptimizerModel ?? "—"}
                                            </span>
                                        </div>
                                        <div>
                                            <label>Racking</label>
                                            <span>{selectedArray.RackingModel ?? "—"}</span>
                                        </div>
                                        <div>
                                            <label>Racking Types</label>
                                            <span>
                                                {Array.isArray(selectedArray.RackingType) ? selectedArray.RackingType.join(", ") : "—"}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="dhv2-array-row">
                                        <div>
                                            <label>Racking Parts</label>
                                            <span className="dhv2-racking-parts">{getRackingPartNames(selectedArray.RackingParts) ?? "—"}</span>
                                        </div>
                                        <div>
                                            <label>Tilt</label>
                                            <span className={isTiltValid ? 'dhv2-value' : 'dhv2-value-error'}>
                                                {selectedArray?.Tilt ?? '—'}
                                            </span>

                                            {!isTiltValid && (
                                                <div className="dhv2-error-message">
                                                Lightreach requires pitch for an array to be between 5° and 50°, please return to the design system and adjust the affected arrays.
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <label>Azimuth</label>
                                            <span>{selectedArray.Azimuth ?? "—"}</span>
                                        </div>
                                    </div>

                                    <div className="dhv2-array-row">
                                        <div>
                                            <label>Module Quantity</label>
                                            <span>{selectedArray.Quantity ?? "—"}</span>
                                        </div>
                                        <div className="overflow-y-auto">
                                            <label>Solar Availability</label>
                                            <span className={selectedArray.SolarAvailability != null && selectedArray.SolarAvailability < 50 ? 'dhv2-value-error' : ''}>
                                                {selectedArray.SolarAvailability ?? "—"}
                                            </span>
                                            {selectedArray.SolarAvailability != null && selectedArray.SolarAvailability < 50 && (
                                                <div className="dhv2-error-message">
                                                    Solar Availability too low, acceptable range is 50-100,Adjust Genesis Design.
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <label>Building Type</label>
                                            <span>
                                                {selectedArray.BuildingType &&
                                                    selectedArray.BuildingType !== "Common"
                                                    ? selectedArray.BuildingType
                                                    : "Roof Mount"}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {arrays.some(arr => !isLightReachTiltValid(arr, financePartner)) && (
                                <div style={{ 
                                    display: 'flex', alignItems: 'center', gap: '8px', 
                                    padding: '8px 12px', borderRadius: '8px',
                                    background: 'rgba(254, 242, 242, 0.9)', 
                                    border: '1px solid rgba(239, 68, 68, 0.2)', 
                                    color: '#b91c1c', fontSize: '0.75rem', fontWeight: 600, 
                                    lineHeight: 1.35, marginTop: '16px', margin: '16px 16px 0 16px' 
                                }}>
                                    <Warning size={16} weight="fill" color="#ef4444" style={{ flexShrink: 0 }} />
                                    <span>One or more arrays have a pitch that is not between 5° and 50°. Lightreach requires pitch for an array to be between 5° and 50°, please return to the design system and adjust the affected arrays.</span>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </DashboardModal>
    );
};

export default DesignHistoryModalV2;
